import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import { IBoardImagesModal } from '@context/Modal/board-images-modal-context';
import { SrcSet } from '@lib/image';
import { SrcSetPicture } from '@components/shared/Picture';
import DeferredImage from '@components/shared/images/atoms/Image/DeferredImage';
import ButtonSvgIcon from '@components/shared/buttons/svg-icon/ButtonSvgIcon';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import NavigatorIconsVersion from '@components/shared/Swiper/NavigatorIconsVersion/NavigatorIconsVersion';
import { IDesignerBoard } from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import { remove } from 'lodash';

import styles from './BoardImagesModal.module.scss';

SwiperCore.use([Navigation]);

interface IBoardImagesModalProps {
  state: IBoardImagesModal;
  onClose: () => void;
}

export default function BoardImagesModal(
  {
    state:
    { boards, boardId },
    onClose
  }: IBoardImagesModalProps
) {
  const theme = useTheme();
  const defaultWidth = 1180;
  const defaultHeight = 700;

  const imageUrls = getImageURLs(boards, boardId);

  return (
    <Modal
      animation
      dialogClassName={styles.ModalDialog}
      show
      centered
      onHide={onClose}
    >
      <div className={classNames(styles.Content, theme.styles.Body)}>
        <CloseButton
          className={styles.Close}
          onClick={onClose}
        />
        <Swiper
          className={styles.swiper}
          navigation={{
            nextEl: `.next`,
            prevEl: `.previous`
          }}
          observer
        >
          {imageUrls.map((url, idx) => (
            <SwiperSlide key={url}>
              <AspectRatioBox
                className={styles.ImageWrapper}
                aspectRatio={defaultHeight / defaultWidth}
              >
                <SrcSetPicture
                  srcSets={getImageSrcSets(url, defaultWidth, defaultHeight)}
                  defaultWidth={defaultWidth}
                  render={({ imgProps }) => (
                    <DeferredImage
                      loadEagerly
                      className={styles.Image}
                      imgProps={{
                        ...imgProps,
                        alt: `Board Image ${idx}`
                      }}
                    />
                  )}
                />
              </AspectRatioBox>
            </SwiperSlide>
          ))}
        </Swiper>
        <NavigatorIconsVersion />
      </div>
    </Modal>
  );
}

interface ICloseButtonProps {
  className?: string;
  onClick?: () => void;
}

function CloseButton({ className, onClick }: ICloseButtonProps) {
  return (
    <ButtonSvgIcon
      classNames={`${styles.CloseButton}${className ? ` ${className}` : ''}`}
      data-test="modal-close-button"
      onClick={onClick}
    >
      <span className="havenly-icon close" />
    </ButtonSvgIcon>
  );
}

function getImageSrcSets(url: string, defaultWidth: number, defaultHeight: number) {
  const srcSets: Record<string, SrcSet> = {};
  ['webp', 'jpeg'].forEach((format) => {
    srcSets[format] =
      new SrcSet({
        sizes: new Map([
          [SrcSet.DEFAULT, [defaultWidth, defaultHeight]],
        ]),
        url,
        props: {
          fit: 'fit-in',
          quality: 100,
          format,
        }
      });
  });

  return srcSets;
}

/**
 * Pluck first two assets to avoid floorplans being shown.
 * Pulls the clicked board image first.
 * @param boards
 * @param boardId: string Sort by this board first
 */
function getImageURLs(
  boards: IDesignerBoard[],
  boardId: string
): string[] {
  const clonedBoards = [...boards];
  const [pulled] = remove(clonedBoards, (b) => b.id === boardId);
  clonedBoards.unshift(pulled);

  return clonedBoards.map((board) => {
    return board.boardAssets.slice(0, 2)
      .map((asset) => asset.asset.url);
  }).flat();
}
